import { useState, useRef } from 'react';
import { Loader2, Camera, Pencil, Check, X, Image as ImageIcon, Trash2 } from 'lucide-react';
import { apiClient } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { workgroupAvatarSrc, workgroupCoverSrc, emitWorkgroupUpdated } from '@/lib/workgroup-avatar';
import { CATEGORY_LABELS, THEME_COLORS, type Workgroup, type WorkgroupCategory, type ThemeColor } from './types';
import BadgePicker, { WorkgroupBadge } from './BadgePicker';
import type { BadgeIconName } from './badge-catalog';

interface Props {
  workgroup: Workgroup;
  canEdit: boolean;
  isOwner: boolean;
  onUpdated: (wg: Workgroup) => void;
}

const THEME_DOT_BG: Record<ThemeColor, string> = {
  blue: 'bg-blue-500',
  emerald: 'bg-emerald-500',
  purple: 'bg-purple-500',
  orange: 'bg-orange-500',
  pink: 'bg-pink-500',
  cyan: 'bg-cyan-500',
  amber: 'bg-amber-500',
  indigo: 'bg-indigo-500',
  red: 'bg-red-500',
  teal: 'bg-teal-500',
};

const THEME_ACCENT_BORDER: Record<ThemeColor, string> = {
  blue: 'border-l-blue-500',
  emerald: 'border-l-emerald-500',
  purple: 'border-l-purple-500',
  orange: 'border-l-orange-500',
  pink: 'border-l-pink-500',
  cyan: 'border-l-cyan-500',
  amber: 'border-l-amber-500',
  indigo: 'border-l-indigo-500',
  red: 'border-l-red-500',
  teal: 'border-l-teal-500',
};

interface AvatarUploadResponse {
  avatar_url: string;
  workgroup: Workgroup;
}

interface CoverUploadResponse {
  cover_image_url: string;
  workgroup: Workgroup;
}

export default function IdentityEditor({ workgroup, canEdit, isOwner, onUpdated }: Props) {
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(workgroup.name);
  const [category, setCategory] = useState<WorkgroupCategory | ''>(workgroup.category || '');
  const [themeColor, setThemeColor] = useState<ThemeColor>((workgroup.theme_color as ThemeColor) || 'blue');
  const [badgeIcon, setBadgeIcon] = useState<BadgeIconName | null>((workgroup.badge_icon as BadgeIconName | null) ?? null);
  const [descriptionMd, setDescriptionMd] = useState(workgroup.description_md || '');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [removingCover, setRemovingCover] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);

  async function handleSave() {
    setSaving(true);
    try {
      const payload: Record<string, unknown> = { name: name.trim(), theme_color: themeColor, badge_icon: badgeIcon };
      if (isOwner) {
        payload.category = category || null;
        payload.description_md = descriptionMd || null;
      }
      const res = await apiClient.patch<Workgroup>(`/messenger/workgroups/${workgroup.id}`, payload);
      if (res.data) onUpdated(res.data);
      setEditing(false);
      toast({ title: 'Gruppo aggiornato', description: 'Le modifiche sono state salvate.' });
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Errore di rete';
      toast({ title: 'Salvataggio fallito', description: msg, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  }

  async function handleCoverUpload(file: File) {
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Formato non valido', description: 'Seleziona un\'immagine (jpg, png, webp).', variant: 'destructive' });
      return;
    }
    setUploadingCover(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await apiClient.post<CoverUploadResponse>(
        `/messenger/workgroups/${workgroup.id}/cover`,
        fd,
      );
      if (!res.data?.workgroup) {
        throw new Error('Risposta server priva del workgroup aggiornato');
      }
      onUpdated(res.data.workgroup);
      emitWorkgroupUpdated(res.data.workgroup);
      toast({ title: 'Copertina aggiornata', description: 'La nuova copertina è ora attiva.' });
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Errore di rete';
      toast({ title: 'Upload copertina fallito', description: msg, variant: 'destructive' });
    } finally {
      setUploadingCover(false);
    }
  }

  async function handleCoverRemove() {
    setRemovingCover(true);
    try {
      const res = await apiClient.patch<Workgroup>(`/messenger/workgroups/${workgroup.id}`, { cover_image_url: null });
      if (res.data) {
        onUpdated(res.data);
        emitWorkgroupUpdated(res.data);
      }
      toast({ title: 'Copertina rimossa' });
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Errore di rete';
      toast({ title: 'Rimozione copertina fallita', description: msg, variant: 'destructive' });
    } finally {
      setRemovingCover(false);
    }
  }

  async function handleAvatarUpload(file: File) {
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Formato non valido', description: 'Seleziona un\'immagine (jpg, png, webp).', variant: 'destructive' });
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await apiClient.post<AvatarUploadResponse>(
        `/messenger/workgroups/${workgroup.id}/avatar`,
        fd,
      );
      if (!res.data?.workgroup) {
        throw new Error('Risposta server priva del workgroup aggiornato');
      }
      onUpdated(res.data.workgroup);
      emitWorkgroupUpdated(res.data.workgroup);
      toast({ title: 'Avatar aggiornato', description: 'La nuova immagine è ora attiva.' });
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Errore di rete';
      toast({ title: 'Upload avatar fallito', description: msg, variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  }

  const accentBorder = THEME_ACCENT_BORDER[themeColor];
  const accentDot = THEME_DOT_BG[themeColor];
  const coverSrc = workgroupCoverSrc(workgroup);

  return (
    <div className="space-y-3">
      {/* Header compatto su superficie neutra, accent solo come border-left.
          Se presente la cover, wash tenue dietro (max ~10% opacity) — no banner pieno. */}
      <div className={`relative overflow-hidden flex items-center gap-3 bg-card border border-border border-l-4 ${accentBorder} rounded-xl px-3 py-2.5`}>
        {coverSrc && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-10 bg-center bg-cover"
            style={{ backgroundImage: `url("${coverSrc}")` }}
          />
        )}
        <div className="relative flex items-center gap-3 w-full">
        <div className="relative shrink-0">
          <div className={`w-12 h-12 rounded-xl bg-muted border border-border flex items-center justify-center text-foreground text-sm font-semibold overflow-hidden`}>
            {workgroup.avatar_url ? (
              <img src={workgroupAvatarSrc(workgroup)} alt="" className="w-full h-full object-cover" />
            ) : (
              <span aria-hidden>{workgroup.name.substring(0, 2).toUpperCase()}</span>
            )}
          </div>
          {canEdit && (
            <>
              <button
                type="button"
                onClick={() => avatarInputRef.current?.click()}
                disabled={uploading}
                aria-label="Cambia avatar gruppo"
                className="absolute -bottom-1 -right-1 bg-primary hover:bg-primary/90 disabled:opacity-60 w-6 h-6 rounded-full flex items-center justify-center ring-2 ring-background"
              >
                {uploading ? <Loader2 size={11} className="animate-spin text-primary-foreground" /> : <Camera size={11} className="text-primary-foreground" />}
              </button>
              <input
                type="file"
                ref={avatarInputRef}
                className="hidden"
                accept="image/*"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleAvatarUpload(f);
                  e.target.value = '';
                }}
              />
            </>
          )}
        </div>

        <div className="flex-1 min-w-0">
          {!editing ? (
            <>
              <div className="flex items-center gap-2">
                <h1 className="text-base font-semibold text-foreground truncate">{workgroup.name}</h1>
                {workgroup.category && (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-border text-muted-foreground shrink-0">
                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${accentDot}`} aria-hidden />
                    {CATEGORY_LABELS[workgroup.category]}
                  </span>
                )}
                {workgroup.badge_icon && (
                  <WorkgroupBadge name={workgroup.badge_icon} themeColor={workgroup.theme_color} size={22} />
                )}
              </div>
              {workgroup.description && (
                <p className="text-xs text-muted-foreground truncate mt-0.5">{workgroup.description}</p>
              )}
            </>
          ) : (
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              className="w-full bg-background border border-border rounded-lg px-2.5 py-1.5 text-foreground text-sm font-medium"
              aria-label="Nome gruppo"
            />
          )}
        </div>

        {canEdit && !editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-card hover:bg-muted text-foreground text-xs font-medium transition"
          >
            <Pencil size={12} /> Modifica
          </button>
        )}
        {canEdit && editing && (
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              type="button"
              onClick={() => { setEditing(false); setName(workgroup.name); setCategory(workgroup.category || ''); setDescriptionMd(workgroup.description_md || ''); setThemeColor((workgroup.theme_color as ThemeColor) || 'blue'); setBadgeIcon((workgroup.badge_icon as BadgeIconName | null) ?? null); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-card hover:bg-muted text-foreground text-xs"
            >
              <X size={12} /> Annulla
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !name.trim()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary hover:bg-primary/90 disabled:opacity-50 text-primary-foreground text-xs font-medium"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
              Salva
            </button>
          </div>
        )}
        </div>
      </div>

      {/* Edit panel: categoria, tema, brief — visibile solo quando editing */}
      {editing && (
        <div className="bg-card border border-border rounded-xl p-3 space-y-3">
          {isOwner && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Categoria</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value as WorkgroupCategory | '')}
                  className="w-full bg-background border border-border rounded-lg px-2.5 py-1.5 text-foreground text-sm"
                >
                  <option value="">—</option>
                  {(Object.keys(CATEGORY_LABELS) as WorkgroupCategory[]).map(c => (
                    <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Tema</label>
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {THEME_COLORS.map(c => (
                    <button
                      type="button"
                      key={c}
                      onClick={() => setThemeColor(c)}
                      className={`w-5 h-5 rounded-full ${THEME_DOT_BG[c]} ring-2 ${themeColor === c ? 'ring-foreground' : 'ring-transparent'} hover:ring-foreground/40 transition`}
                      aria-label={`Tema ${c}`}
                      aria-pressed={themeColor === c}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
          {canEdit && (
            <div>
              <label className="block text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Badge identificativo</label>
              <BadgePicker value={badgeIcon} onChange={setBadgeIcon} themeColor={themeColor} size="sm" />
            </div>
          )}
          {canEdit && (
            <div>
              <label className="block text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Copertina</label>
              <div className="flex items-center gap-2">
                <div className="w-16 h-10 rounded-md border border-border bg-muted overflow-hidden shrink-0 flex items-center justify-center">
                  {coverSrc ? (
                    <img src={coverSrc} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon size={14} className="text-muted-foreground" aria-hidden />
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => coverInputRef.current?.click()}
                  disabled={uploadingCover || removingCover}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-border bg-card hover:bg-muted disabled:opacity-60 text-foreground text-xs"
                >
                  {uploadingCover ? <Loader2 size={12} className="animate-spin" /> : <ImageIcon size={12} />}
                  {workgroup.cover_image_url ? 'Cambia copertina' : 'Carica copertina'}
                </button>
                {workgroup.cover_image_url && (
                  <button
                    type="button"
                    onClick={handleCoverRemove}
                    disabled={uploadingCover || removingCover}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-border bg-card hover:bg-muted disabled:opacity-60 text-muted-foreground hover:text-foreground text-xs"
                  >
                    {removingCover ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                    Rimuovi
                  </button>
                )}
                <input
                  type="file"
                  ref={coverInputRef}
                  className="hidden"
                  accept="image/*"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleCoverUpload(f);
                    e.target.value = '';
                  }}
                />
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">Mostrata come sfondo tenue dietro l'intestazione del gruppo.</p>
            </div>
          )}
          {isOwner && (
            <div>
              <label className="block text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Brief (markdown)</label>
              <textarea
                value={descriptionMd}
                onChange={(e) => setDescriptionMd(e.target.value)}
                rows={4}
                className="w-full bg-background border border-border rounded-lg px-2.5 py-2 text-foreground text-sm font-mono"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
